import React, { useState, useEffect } from "react";
import { Button, Form, FormGroup, Label } from "reactstrap";
import { useForm, FormProvider, Controller } from "react-hook-form";

import { useFromUser } from "stores/LocalStorageHelper";
import { saveSuccessAlert } from "utils/alertUtils";
import InputElement, {
	CurrencyInputElement,
	SelectorElement,
} from "features/forms/components/InputElement";
import InputWrapper from "features/forms/components/InputWrapper";
import { DealStatus, isTaxableOption } from "constants/Constants";
import shallow from "zustand/shallow";
import CreatableSelect from "react-select/creatable";
import useCurrentDealStore from "stores/DealStore";
import PropTypes from "prop-types";

/**
 * DMVFeeForms component to handle adding and editing DMV fees.
 *
 * @param {Object} props - The component props.
 * @param {number} props.currentIndex - The current index of the DMV fee being edited.
 * @param {function} props.setCurrentIndex - Function to set the current index.
 * @param {function} props.toggle - Function to toggle the form visibility.
 * @param {Array} props.optionList - List of options for the DMV fee type.
 */
export const DMVFeeForms = ({
	currentIndex,
	setCurrentIndex,
	toggle,
	optionList,
}) => {
	const {
		miscDMVFees,
		editMiscDMVFees,
		dealRecNum,
		type,
		companies,
		calculate,
	} = useCurrentDealStore(
		(state) => ({
			miscDMVFees: state.miscDMVFees,
			editMiscDMVFees: state.editMiscDMVFees,
			dealRecNum: state.deal.ID,
			type: state.deal.type,
			companies: state.dmvFeeCompanies,
			calculate: state.calculate,
		}),
		shallow
	);

	const dealerID = useFromUser("dealerID");
	const locationID = useFromUser("locationID");

	const [description, setDescription] = useState("");
	const [amount, setAmount] = useState(0);
	const [company, setCompany] = useState("");
	const [cost, setCost] = useState(0);
	const [dmvFeeType, setDmvFeeType] = useState("");
	const [buttonText, setButtonText] = useState("Add Fee");
	const [isTaxable, setIsTaxable] = useState(false);
	const changeText = (text) => setButtonText(text);

	const clearFields = () => {
		changeText("Add Fee");
		setDescription("");
		setAmount(0);
		setCompany("");
		setIsTaxable(false);
		setCost(0);
		//setCurrentIndex(miscDMVFees.length);
		setDmvFeeType("OTHER-DMV");
	};

	const addMiscFee = () => {
		const getID = () => {
			if (miscDMVFees[currentIndex] === undefined) {
				return null;
			} else if (miscDMVFees[currentIndex].ID === undefined) {
				return null;
			} else {
				return miscDMVFees[currentIndex].ID;
			}
		};
		const ID = getID();
		const newMiscFee = {
			type: dmvFeeType || "OTHER-DMV",
			description,
			amount: parseFloat(amount),
			company,
			cost: parseFloat(cost),
			dealerID,
			locationID,
			ID,
			dealRecNum,
			isTaxable,
		};
		console.log(newMiscFee);
		console.log("Current index is: " + currentIndex);

		const miscList = () => {
			let obj = [...miscDMVFees]; // Create a copy of miscDMVFees
			obj[currentIndex] = newMiscFee;
			return obj;
		};
		const newMiscFeeObj = miscList();
		editMiscDMVFees(newMiscFeeObj);
		clearFields();
		saveSuccessAlert();
		calculate();
		toggle();
	};

	const taxableHandler = (value) => {
		setIsTaxable(value === "true");
	};

	const methods = useForm();

	useEffect(() => {
		if (currentIndex < miscDMVFees.length) {
			setDmvFeeType(miscDMVFees[currentIndex]?.type || "OTHER-DMV");
			setAmount(miscDMVFees[currentIndex]?.amount || 0);
			setCompany(miscDMVFees[currentIndex]?.company || "");
			setCost(miscDMVFees[currentIndex]?.cost || 0);
			setDescription(miscDMVFees[currentIndex]?.description || "");
			setIsTaxable(miscDMVFees[currentIndex]?.isTaxable || false);
			changeText("Save Fee");
		} else {
			clearFields();
		}
	}, [currentIndex]);

	return (
		<FormProvider {...methods}>
			<Form onSubmit={methods.handleSubmit(addMiscFee)}>
				<InputWrapper
					formTitle="Add Fee"
					inputComponents={[
						<SelectorElement
							key="DMVFeeType"
							{...methods}
							name="DMVFeeType"
							label="DMV Fee Type"
							value={dmvFeeType}
							onChange={(e) => setDmvFeeType(e.target.value)}
							options={optionList}
							labelClass="required"
							colSize="col-6"
						/>,
						<Controller
							key="DMVCompanies"
							control={methods.control}
							name="DMVCompanies"
							defaultValue={company || ""}
							render={({ field }) => (
								<FormGroup className="col-md-9">
									<Label>Company</Label>
									<CreatableSelect
										{...field}
										value={{ value: company, label: company }}
										options={companies.map((value) => ({
											value: value,
											label: value,
										}))}
										onChange={(e) => {
											setCompany(e.value);
										}}
									/>
								</FormGroup>
							)}
						/>,
						<SelectorElement
							key="DMVFeeTaxable"
							readOnly={type !== DealStatus.PENDING}
							{...methods}
							name="DMVFeeTaxable"
							label="Taxable"
							value={isTaxable}
							onChange={(e) => taxableHandler(e.target.value)}
							options={isTaxableOption}
						/>,
						<InputElement
							key="DMVFeeDescription"
							readOnly={type !== DealStatus.PENDING}
							{...methods}
							value={description}
							onChange={(e) => {
								setDescription(e.target.value);
							}}
							name="DMVFeeDescription"
							label="Description"
							type="text"
							colSize="col-sm-12"
						/>,
						<CurrencyInputElement
							key="DMVFeeCost"
							readOnly={type !== DealStatus.PENDING}
							value={cost}
							onChange={setCost}
							name="DMVFeeCost"
							label="Cost"
							type="number"
						/>,
						<CurrencyInputElement
							key="DMVFeeAmount"
							readOnly={type !== DealStatus.PENDING}
							value={amount}
							onChange={setAmount}
							name="DMVFeeAmount"
							label="Amount"
							type="number"
						/>,
					]}
					buttons={
						<div className="d-flex justify-content-center mt-2">
							<Button color="primary" size="sm" type="submit">
								{buttonText === "Save Fee" ? (
									<i className="nc-icon nc-check-2"></i>
								) : (
									<i className="nc-icon nc-simple-add"></i>
								)}
								{buttonText}
							</Button>
							<div className="btn btn-link btn-md" onClick={clearFields}>
								Reset
							</div>
						</div>
					}
				/>
			</Form>
		</FormProvider>
	);
};

DMVFeeForms.propTypes = {
	currentIndex: PropTypes.number.isRequired,
	setCurrentIndex: PropTypes.func.isRequired,
	toggle: PropTypes.func.isRequired,
	optionList: PropTypes.array.isRequired,
};

export default DMVFeeForms;
